<script>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useResizeObserver } from '@vueuse/core';

// Estimativa da largura do badge `+N`, usada so enquanto ele nunca esteve na
// tela. Ele e reservado antes de decidir o corte, senao a ultima etiqueta cabe
// na conta e o badge, que entra depois dela, a empurra para fora.
//
// Um pouco folgada de proposito: reservar demais custa alguns pixels, reservar
// de menos deixa o badge estourar o container e ser cortado pelo
// `overflow-hidden`. Assim que o badge renderiza uma vez, a medida real toma o
// lugar dela (`badgeWidth`).
const BADGE_WIDTH_FALLBACK = 30;

// Geometria do badge `+N`, derivada do CSS dele (`h-5 px-2 border text-xxs`):
// 8px de padding de cada lado (`px-2` na escala de padding) e 1px de borda de
// cada lado, mais um avanco por caractere a 10px de fonte.
//
// O numero e copia do CSS, entao anda com ele: quando `px-2` mudou de 6 para
// 8px na ancoragem da escala na grade de 4px, esta reserva teve que subir de 14
// para 18. Reservar de menos e o caso ruim -- o badge estoura o container e o
// `overflow-hidden` corta o numero.
//
// Serve para reservar pelo PIOR caso. A largura medida vale para o texto que o
// badge tinha na passada anterior, e numa lista virtualizada a linha e
// reaproveitada: uma que mostrava `+9` (~26px) podia ser rebindada a uma
// conversa que precisa de `+12` (~31px), a reserva ficava curta e o badge era
// cortado ao meio pelo `overflow-hidden` -- o agente lia `+1`.
//
// Reservar demais custa alguns pixels; reservar de menos mente o numero.
const BADGE_PADDING = 18;
const BADGE_CHAR_WIDTH = 6;

const worstCaseBadgeWidth = total =>
  BADGE_PADDING +
  (1 + String(Math.max(total - 1, 1)).length) * BADGE_CHAR_WIDTH;

export default {
  props: {
    conversationLabels: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const accountLabels = useMapGetter('labels/getLabels');
    const labelContainer = ref(null);

    const activeLabels = computed(() => {
      return accountLabels.value.filter(({ title }) =>
        props.conversationLabels.includes(title)
      );
    });

    return {
      activeLabels,
      labelContainer,
    };
  },
  data() {
    return {
      labelPosition: -1,
      isComputing: false,
      resizeObserver: null,
      observedContainer: null,
      // Largura medida do badge `+N`. Fica nula ate ele aparecer pela primeira
      // vez; dai em diante e ela que manda, no lugar da estimativa.
      badgeWidth: null,
      // Popup com as etiquetas que nao couberam. Coordenadas de viewport porque
      // o woot-context-menu se move para o <body> e usa `position: fixed`.
      overflowMenu: { open: false, x: 0, y: 0 },
      // Ver onOverflowBadgeMouseDown.
      wasOverflowMenuOpen: false,
    };
  },
  computed: {
    // O que sobrou depois do corte. `labelPosition` e o indice da ultima
    // etiqueta que coube; -1 significa que nenhuma coube.
    hiddenLabels() {
      return this.activeLabels.slice(this.labelPosition + 1);
    },
  },
  watch: {
    activeLabels() {
      this.closeOverflowMenu();
      this.$nextTick(() => {
        // O `v-if` da raiz depende de haver etiqueta, entao o container so
        // nasce agora se o card montou sem nenhuma -- e o observer amarrado no
        // `mounted` teria ficado presa a `null`, sem nunca religar. A partir
        // dai a linha nao reagia mais a redimensionamento e mantinha um corte
        // velho ate a lista virtual reciclar aquela linha.
        this.observeContainer();
        this.computeVisibleLabelPosition();
      });
    },
  },
  mounted() {
    this.computeVisibleLabelPosition();
    this.observeContainer();
    // `capture`: a lista de conversas rola num container proprio, e evento de
    // scroll de elemento nao borbulha ate a window.
    window.addEventListener('scroll', this.onViewportScroll, true);
  },
  beforeDestroy() {
    this.stopObservingContainer();
    window.removeEventListener('scroll', this.onViewportScroll, true);
  },
  methods: {
    observeContainer() {
      const container = this.labelContainer;
      if (!container || container === this.observedContainer) return;

      this.stopObservingContainer();
      this.observedContainer = container;
      this.resizeObserver = useResizeObserver(container, () => {
        this.computeVisibleLabelPosition();
      });
    },
    stopObservingContainer() {
      if (this.resizeObserver) this.resizeObserver.stop();
      this.resizeObserver = null;
      this.observedContainer = null;
    },
    // Lembra se o popup estava aberto ANTES do foco sair dele.
    //
    // O ContextMenu foca a si mesmo e emite `close` no proprio `blur`. Clicar
    // no badge para fechar disparava esse blur ainda no `mousedown`, entao
    // quando o `click` chegava `overflowMenu.open` ja era `false` e o toggle
    // reabria o popup no mesmo lugar: ele parecia travado aberto, e como a
    // linha inteira navega para a conversa, so dava para sair clicando fora.
    //
    // Registrar o estado no mousedown -- antes do blur -- e o que devolve ao
    // clique a informacao que ele perdeu.
    onOverflowBadgeMouseDown() {
      this.wasOverflowMenuOpen = this.overflowMenu.open;
    },
    // O `stopPropagation` e obrigatorio: o card inteiro e clicavel e navega
    // para a conversa. Sem ele, abrir a lista de etiquetas trocaria de tela.
    toggleOverflowMenu(e) {
      e.stopPropagation();

      const estavaAberto = this.wasOverflowMenuOpen;
      this.wasOverflowMenuOpen = false;

      if (this.overflowMenu.open || estavaAberto) {
        this.closeOverflowMenu();
        return;
      }

      // Ancora no proprio badge, em coordenadas de viewport: o popup vai para o
      // <body> justamente para escapar do `overflow-hidden` da lista de
      // conversas, entao coordenada relativa nao serviria. O ContextMenu ajusta
      // sozinho se nao couber para baixo ou para a direita.
      const badge = this.$refs.overflowBadge;
      const rect = badge.getBoundingClientRect();

      this.overflowMenu = { open: true, x: rect.left, y: rect.bottom + 4 };
    },
    closeOverflowMenu() {
      this.overflowMenu = { ...this.overflowMenu, open: false };
    },
    // As coordenadas sao de viewport e o ContextMenu so as le ao montar, entao
    // rolar a lista deixava o popup parado na tela, flutuando sobre linhas de
    // outras conversas. Rolar com a roda nao tira o foco dele, logo o `blur`
    // que ele ja escuta nunca chegava. Fechar e mais honesto que perseguir o
    // badge: a lista e virtualizada e a linha de origem pode nem existir mais.
    onViewportScroll() {
      if (this.overflowMenu.open) this.closeOverflowMenu();
    },
    // Mede POSICAO real, nao soma de larguras estimadas.
    //
    // A versao anterior somava `getBoundingClientRect().width + 8` de cada
    // etiqueta e comparava com a largura do container. A conta errava para mais:
    // as etiquetas escondidas ficam `invisible absolute`, e nesse estado o
    // `max-w-[calc(100%-0.5rem)]` delas passa a se referir a outro bloco
    // contentor, entao a largura medida nao era a largura em fluxo. Com isso o
    // algoritmo concluia que as 6 cabiam, nao mostrava badge, e o
    // `overflow-hidden` comia a sexta em silencio -- o defeito relatado.
    //
    // Agora o ciclo e: mostra todas, deixa o navegador posicionar, e le a borda
    // direita de cada uma. Quem passa do limite e cortado. Layout real em vez de
    // aritmetica sobre elementos fora de fluxo.
    computeVisibleLabelPosition() {
      if (this.isComputing) return;
      this.isComputing = true;

      // Largura real do badge, lida ANTES de recolocar tudo em fluxo: neste
      // instante ele ainda esta na tela, se havia excedente. Um passo adiante
      // ele desaparece e nao havera o que medir.
      const badge = this.$refs.overflowBadge;
      if (badge) this.badgeWidth = badge.getBoundingClientRect().width;

      const total = this.activeLabels.length;
      if (!total) {
        this.labelPosition = -1;
        this.isComputing = false;
        return;
      }

      // Passo 1: tudo em fluxo, para o navegador dar as posicoes de verdade.
      //
      // Isto ja e um commit, e por isso qualquer desistencia daqui para baixo
      // tem de desfaze-lo. Sem isso, medir com o container em largura zero --
      // a lista escondida no layout mobile, o painel de contato recolhido --
      // deixava a linha com TODAS as etiquetas visiveis e sem badge, e o
      // `overflow-hidden` cortava as excedentes em silencio: exatamente o
      // defeito que este calculo existe para evitar.
      const posicaoAnterior = this.labelPosition;
      const desistir = () => {
        this.labelPosition = posicaoAnterior;
        this.isComputing = false;
      };

      this.labelPosition = total - 1;

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const container = this.labelContainer;
          if (!container) {
            desistir();
            return;
          }

          const limit = container.clientWidth;
          const labels = Array.from(container.querySelectorAll('.label'));

          if (!limit || !labels.length) {
            desistir();
            return;
          }

          // Quanto do container a etiqueta consome, da borda inicial ate o fim
          // dela.
          //
          // Era `offsetLeft + offsetWidth`, e `offsetLeft` conta a partir do
          // ancestral POSICIONADO -- que aqui e o card inteiro (`relative`, por
          // causa da barra da conversa ativa), nao este container. A conta
          // somava tudo o que vem antes na linha, o chip do canal em primeiro
          // lugar, e comparava esse total com a largura de um container que
          // comeca justamente depois disso. O corte saia cedo demais: com 228px
          // de container e uma etiqueta de 43px, a seguinte ja era escondida
          // com 184px livres na linha.
          const box = container.getBoundingClientRect();
          const isRtl = getComputedStyle(container).direction === 'rtl';
          const fits = (el, room) => {
            const rect = el.getBoundingClientRect();
            const usado = isRtl ? box.right - rect.left : rect.right - box.left;

            return usado <= room;
          };

          // Passo 2: sem badge, todas cabem?
          const lastFitting = room =>
            labels.findIndex(el => !fits(el, room)) - 1;

          let position = lastFitting(limit);
          if (position === -2) position = labels.length - 1; // nenhuma estourou

          // Passo 3: se sobrou alguma, o badge entra na linha e ocupa espaco,
          // entao o corte precisa ser refeito ja descontando ele -- mais a
          // margem que cada chip carrega, que e o que separa os dois.
          if (position < labels.length - 1) {
            const margem =
              parseFloat(
                getComputedStyle(labels[0])[
                  isRtl ? 'marginLeft' : 'marginRight'
                ]
              ) || 0;
            const reserva =
              Math.max(
                this.badgeWidth || BADGE_WIDTH_FALLBACK,
                worstCaseBadgeWidth(labels.length)
              ) + margem;

            position = lastFitting(limit - reserva);
            if (position === -2) position = labels.length - 1;
          }

          // Pelo menos uma etiqueta sempre aparece. Um nome muito longo nao
          // cabia nem sozinho, o corte zerava e o card mostrava so o contador
          // (`+3` sem etiqueta nenhuma) -- sem pista do que havia ali. O teto
          // de largura da etiqueta garante que ela caiba truncada, com o
          // contador ao lado, e o tooltip do chip mostra o nome inteiro.
          this.labelPosition = Math.max(position, 0);
          this.isComputing = false;
        });
      });
    },
  },
};
</script>

<template>
  <div
    v-if="activeLabels.length"
    ref="labelContainer"
    class="w-full max-w-full overflow-hidden"
  >
    <div class="flex items-center flex-shrink min-w-0 w-full h-5 flex-nowrap">
      <woot-label
        v-for="(label, index) in activeLabels"
        :key="label ? label.id : index"
        :title="label.title"
        :description="label.description"
        :color="label.color"
        variant="smooth"
        class="!mb-0 shrink-0 max-w-[calc(100%-2.5rem)]"
        small
        :class="{ hidden: index > labelPosition }"
      />
      <button
        v-if="hiddenLabels.length"
        ref="overflowBadge"
        type="button"
        :title="$t('CONVERSATION.CARD.SHOW_LABELS')"
        class="labels-overflow"
        @mousedown="onOverflowBadgeMouseDown"
        @click="toggleOverflowMenu"
      >
        +{{ hiddenLabels.length }}
      </button>
    </div>

    <woot-context-menu
      v-if="overflowMenu.open"
      :x="overflowMenu.x"
      :y="overflowMenu.y"
      @close="closeOverflowMenu"
    >
      <div class="labels-overflow__pane" @click.stop>
        <woot-label
          v-for="label in hiddenLabels"
          :key="`hidden-${label.id}`"
          :title="label.title"
          :description="label.description"
          :color="label.color"
          variant="smooth"
          class="!mb-0 max-w-full"
          small
        />
      </div>
    </woot-context-menu>
  </div>
</template>

<style lang="scss" scoped>
// Contador do que nao coube. Mesma geometria do `woot-label small` que ele
// acompanha na linha (h-5, pill, text-xxs), em tom neutro para nao competir com
// as cores das etiquetas de verdade.
.labels-overflow {
  @apply inline-flex items-center h-5 px-2 shrink-0 rounded-full border
    border-line bg-surface-sunken text-muted text-xxs font-medium
    hover:bg-surface-hover hover:text-body transition-colors;
}

// O popup vai para o <body> pelo woot-context-menu, entao ele nao herda nada do
// card -- dai a superficie e a borda explicitas aqui.
.labels-overflow__pane {
  @apply flex flex-wrap items-center gap-1 max-w-[16rem] p-2 rounded-lg border
    border-line bg-surface-raised;
}

// `invisible absolute` e nao `display:none`: a etiqueta precisa continuar
// medivel, porque e da largura dela que a conta de quantas cabem depende.
.hidden {
  @apply invisible absolute;
}
</style>
